/**
 * Master Symbols Management Page - Enhanced with Modern UI and Exchange Tabs
 * Allows users to create and manage master symbols with improved UX
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

const MasterSymbols = () => {
  const { userSession } = useSelector((state) => state.auth);
  const [masterSymbols, setMasterSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [activeExchange, setActiveExchange] = useState('NSE');
  const [formData, setFormData] = useState({
    symbol_name: '',
    display_name: '',
    description: '',
    exchange: 'NSE'
  });

  // Autocomplete states
  const [symbolSuggestions, setSymbolSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingSymbols, setSearchingSymbols] = useState(false);

  const exchanges = [
    { id: 'NSE', name: 'NSE', label: 'National Stock Exchange', icon: '📈', color: 'blue' },
    { id: 'NFO', name: 'NFO', label: 'NSE F&O', icon: '📊', color: 'purple' },
    { id: 'BSE', name: 'BSE', label: 'Bombay Stock Exchange', icon: '🏢', color: 'green' },
    { id: 'BFO', name: 'BFO', label: 'BSE F&O', icon: '📉', color: 'orange' },
    { id: 'MCX', name: 'MCX', label: 'Multi Commodity Exchange', icon: '🥇', color: 'yellow' },
    { id: 'CDS', name: 'CDS', label: 'Currency Derivatives', icon: '💱', color: 'indigo' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/master-symbols', {
          headers: { Authorization: `Bearer ${userSession}` }
        });

        if (response.data.success) {
          setMasterSymbols(response.data.data || []);
        } else {
          setError(response.data.error);
        }
      } catch (err) {
        console.error('Failed to fetch master symbols:', err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userSession]);

  const fetchMasterSymbols = async () => {
    try {
      setLoading(true);
      const response = await api.get('/master-symbols', {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        setMasterSymbols(response.data.data || []);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      console.error('Failed to fetch master symbols:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Debounced symbol search function
  const searchSymbols = async (query, exchange) => {
    if (query.length < 3) {
      setSymbolSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setSearchingSymbols(true);
      const response = await api.get('/symbols/search', {
        params: {
          q: query,
          exchange: exchange,
          limit: 20
        },
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        setSymbolSuggestions(response.data.data || []);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error('Symbol search error:', err);
    } finally {
      setSearchingSymbols(false);
    }
  };

  // Debounce timer
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSymbolNameChange = (value) => {
    const upperValue = value.toUpperCase();
    setFormData({...formData, symbol_name: upperValue});

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    if (upperValue.length >= 3) {
      const timeout = setTimeout(() => {
        searchSymbols(upperValue, formData.exchange);
      }, 300); // 300ms debounce
      setSearchTimeout(timeout);
    } else {
      setSymbolSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSymbolSuggestion = (symbol) => {
    setFormData({
      ...formData,
      symbol_name: symbol.tradingSymbol,
      display_name: symbol.instrumentName || symbol.tradingSymbol,
      description: `${symbol.instrumentType || ''} ${symbol.lotSize ? `(Lot: ${symbol.lotSize})` : ''}`.trim()
    });
    setShowSuggestions(false);
    setSymbolSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingSymbol ? `/master-symbols/${editingSymbol.id}` : '/master-symbols';
      const method = editingSymbol ? 'put' : 'post';
      
      const response = await api[method](url, formData, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        alert(`Master symbol ${editingSymbol ? 'updated' : 'created'} successfully!`);
        setShowForm(false);
        setEditingSymbol(null);
        setFormData({ symbol_name: '', display_name: '', description: '', exchange: 'NSE' });
        fetchMasterSymbols();
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to save master symbol: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (symbol) => {
    setEditingSymbol(symbol);
    setFormData({
      symbol_name: symbol.symbol_name,
      display_name: symbol.display_name,
      description: symbol.description || '',
      exchange: symbol.exchange
    });
    setActiveExchange(symbol.exchange);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this master symbol?')) {
      return;
    }

    try {
      const response = await api.delete(`/master-symbols/${id}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        alert('Master symbol deleted successfully!');
        fetchMasterSymbols();
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete master symbol: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleToggleActive = async (symbol) => {
    const newStatus = symbol.is_active ? 0 : 1;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} "${symbol.symbol_name}" (${symbol.exchange})?`)) {
      return;
    }

    try {
      const response = await api.put(`/master-symbols/${symbol.id}`, {
        ...symbol,
        is_active: newStatus
      }, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        alert(`Master symbol ${action}d successfully!`);
        fetchMasterSymbols();
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (err) {
      console.error('Toggle active error:', err);
      alert(`Failed to ${action} master symbol: ` + (err.response?.data?.error || err.message));
    }
  };

  // Group symbols by exchange
  const symbolsByExchange = masterSymbols.reduce((acc, symbol) => {
    if (!acc[symbol.exchange]) {
      acc[symbol.exchange] = [];
    }
    acc[symbol.exchange].push(symbol);
    return acc;
  }, {});

  // Get current exchange data
  const currentExchangeData = exchanges.find(ex => ex.id === activeExchange);
  const currentSymbols = symbolsByExchange[activeExchange] || [];

  const getExchangeStats = () => {
    return exchanges.map(exchange => ({
      ...exchange,
      count: symbolsByExchange[exchange.id]?.length || 0,
      active: symbolsByExchange[exchange.id]?.filter(s => s.is_active).length || 0
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading master symbols...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto p-2">
        {/* Header */}
        <div className="text-center py-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-4 shadow-lg">
              <span className="text-2xl">📋</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Master Symbols
              </h1>
              <p className="text-gray-600">Manage trading symbols across exchanges</p>
            </div>
          </div>
        </div>

        {/* Exchange Tabs */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 mb-6 mx-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                <span className="text-lg">🏢</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Exchange Management</h2>
                <p className="text-xs text-gray-600">Select an exchange to manage symbols</p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingSymbol(null);
                setFormData({ symbol_name: '', display_name: '', description: '', exchange: activeExchange });
                setShowForm(!showForm);
              }}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center"
            >
              <span className="mr-2">➕</span>
              Add Symbol
            </button>
          </div>

          {/* Exchange Tabs Navigation */}
          <div className="flex flex-wrap gap-2 mb-4">
            {getExchangeStats().map((exchange) => (
              <button
                key={exchange.id}
                onClick={() => setActiveExchange(exchange.id)}
                className={`relative flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 ${
                  activeExchange === exchange.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white/60 text-gray-700 hover:bg-white/80 border border-gray-200'
                }`}
              >
                <span className="text-lg mr-2">{exchange.icon}</span>
                <div className="text-left">
                  <div className="font-semibold">{exchange.name}</div>
                  <div className="text-xs opacity-80">
                    {exchange.count} symbols • {exchange.active} active
                  </div>
                </div>
                {activeExchange === exchange.id && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full"></div>
                )}
              </button>
            ))}
          </div>

          {/* Current Exchange Info */}
          {currentExchangeData && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center">
                    <span className="mr-2">{currentExchangeData.icon}</span>
                    {currentExchangeData.label}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {currentSymbols.length} total symbols • {currentSymbols.filter(s => s.is_active).length} active • {currentSymbols.filter(s => !s.is_active).length} inactive
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    {currentSymbols.length}
                  </div>
                  <div className="text-xs text-gray-500">Symbols</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 mx-2 shadow-lg">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white font-bold">!</span>
              </div>
              <div>
                <h3 className="font-semibold text-red-800">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 mb-6 mx-2">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-lg">{editingSymbol ? '📝' : '➕'}</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {editingSymbol ? 'Edit Master Symbol' : 'Add New Master Symbol'}
                  </h2>
                  <p className="text-sm text-gray-600">Configure symbol details for {currentExchangeData?.label}</p>
                </div>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    🏷️ Symbol Name *
                  </label>
                  <input
                    type="text"
                    value={formData.symbol_name}
                    onChange={(e) => handleSymbolNameChange(e.target.value)}
                    onFocus={() => {
                      if (formData.symbol_name.length >= 3 && symbolSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    placeholder="e.g., NIFTY, BANKNIFTY, RELIANCE"
                    disabled={editingSymbol}
                    required
                    autoComplete="off"
                  />
                  {searchingSymbols && (
                    <div className="absolute right-3 top-11 text-blue-500">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                  
                  {/* Autocomplete Dropdown */}
                  {showSuggestions && symbolSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                      <div className="p-2 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-200">
                        <p className="text-xs text-gray-600 font-semibold">
                          💡 Found {symbolSuggestions.length} symbols - Click to select
                        </p>
                      </div>
                      {symbolSuggestions.map((symbol, index) => (
                        <div
                          key={index}
                          onClick={() => selectSymbolSuggestion(symbol)}
                          className="px-4 py-3 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 cursor-pointer border-b border-gray-100 transition-all duration-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 text-sm">
                                {symbol.tradingSymbol}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {symbol.instrumentName}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                                  {symbol.exchange}
                                </span>
                                {symbol.instrumentType && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-medium">
                                    {symbol.instrumentType}
                                  </span>
                                )}
                                {symbol.lotSize && (
                                  <span className="text-xs text-gray-500">
                                    Lot: {symbol.lotSize}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1 flex items-center">
                    <span className="mr-1">💡</span>
                    {formData.symbol_name.length < 3 
                      ? 'Type at least 3 characters to search symbols'
                      : 'Simple name without suffixes (e.g., NIFTY instead of NIFTY50-EQ)'
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    🏢 Exchange *
                  </label>
                  <select
                    value={formData.exchange}
                    onChange={(e) => {
                      const newExchange = e.target.value;
                      setFormData({...formData, exchange: newExchange});
                      // Re-search if there's a symbol name with 3+ characters
                      if (formData.symbol_name.length >= 3) {
                        searchSymbols(formData.symbol_name, newExchange);
                      }
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    required
                  >
                    {exchanges.map(exchange => (
                      <option key={exchange.id} value={exchange.id}>
                        {exchange.icon} {exchange.name} - {exchange.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    📝 Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    placeholder="e.g., Nifty 50, Bank Nifty"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    📋 Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm"
                    placeholder="Optional description..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-xl transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  {editingSymbol ? 'Update Symbol' : 'Create Symbol'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Symbols Display */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-6 mx-2">
          {currentSymbols.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📎</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No symbols found for {currentExchangeData?.label}
              </h3>
              <p className="text-gray-500 mb-4">
                Add some symbols to get started with {activeExchange} exchange.
              </p>
              <button
                onClick={() => {
                  setEditingSymbol(null);
                  setFormData({ symbol_name: '', display_name: '', description: '', exchange: activeExchange });
                  setShowForm(true);
                }}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-2 px-4 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Add First Symbol
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-lg text-white">{currentExchangeData?.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {currentExchangeData?.label} Symbols
                    </h3>
                    <p className="text-sm text-gray-600">
                      {currentSymbols.length} total • {currentSymbols.filter(s => s.is_active).length} active
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Symbol Details
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentSymbols.map((symbol) => (
                      <tr key={symbol.id} className={`transition-all duration-200 hover:bg-blue-50/50 ${
                        !symbol.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'
                      }`}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{symbol.symbol_name}</div>
                            <div className="text-sm text-gray-600">{symbol.display_name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-700">
                            {symbol.description || <span className="text-gray-400 italic">No description</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            symbol.is_active 
                              ? 'bg-green-100 text-green-800 border border-green-200' 
                              : 'bg-red-100 text-red-800 border border-red-200'
                          }`}>
                            <span className={`w-2 h-2 rounded-full mr-2 ${
                              symbol.is_active ? 'bg-green-400' : 'bg-red-400'
                            }`}></span>
                            {symbol.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">
                            {new Date(symbol.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleToggleActive(symbol)}
                              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all duration-200 ${
                                symbol.is_active 
                                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                                  : 'bg-green-500 hover:bg-green-600 text-white'
                              }`}
                              title={symbol.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {symbol.is_active ? '⏸️ Pause' : '▶️ Activate'}
                            </button>
                            <button
                              onClick={() => handleEdit(symbol)}
                              className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-all duration-200"
                              disabled={!symbol.is_active}
                            >
                              📝 Edit
                            </button>
                            <button
                              onClick={() => handleDelete(symbol.id)}
                              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold transition-all duration-200"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterSymbols;